import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../utils/api";
import { API_ENDPOINTS } from "../../constants/api";
import LawyerRow from "./LawyerRow";
import "./UserManagement.css";

function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  const [newUserData, setNewUserData] = useState({
    email: "",
    password: "",
    role: 1,
  });
  const [creatingUser, setCreatingUser] = useState(false);
  const [deletingUser, setDeletingUser] = useState(null);
  const [confirmingDelete, setConfirmingDelete] = useState(null);
  const [editingRole, setEditingRole] = useState(null);
  const [updatingRole, setUpdatingRole] = useState(null);
  const [newRoleData, setNewRoleData] = useState({
    role: 1,
    roleName: "Lawyer",
  });

  // API call to fetch all lawyers/users
  const fetchUserData = async () => {
    setLoading(true);
    setError(null);

    try {
      const clientDataPromise = api.get(API_ENDPOINTS.CLIENTS.GET_ALL);

      clientDataPromise.then((clientResponse) => {
        const clients = clientResponse.data.clients || [];
        setClients(clients);
      });

      const response = await api.get(API_ENDPOINTS.LAWYERS.GET_ALL);
      const lawyers = response.data.lawyers;

      // Transform the lawyer data to match our component structure
      const transformedUsers = lawyers.map((lawyer) => ({
        id: lawyer._id,
        name: lawyer.email.split("@")[0],
        email: lawyer.email,
        role: lawyer.role,
        roleLabel: lawyer.role_name,
        lastActive: "N/A",
        assignedClients: lawyer.assigned_clients.length,
        assignedClientsList: lawyer.assigned_clients,
        createdAt: lawyer.created_at || "N/A",
        rawData: lawyer,
      }));

      setUsers(transformedUsers);
      toast.success(`${lawyers.length} users loaded successfully`, {
        position: "top-right",
        autoClose: 3000,
      });
    } catch (err) {
      console.error("Failed to fetch users:", err);
      setError("Failed to load users. Please try again.");
      toast.error("Failed to load users", {
        position: "top-right",
        autoClose: 5000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch users on component mount
  useEffect(() => {
    fetchUserData();
  }, []);

  // Handle expand/collapse row
  const toggleRowExpansion = (userId) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(userId)) {
        newSet.delete(userId);
      } else {
        newSet.add(userId);
      }
      return newSet;
    });
  };

  // Check if row is expanded
  const isRowExpanded = (userId) => {
    return expandedRows.has(userId);
  };

  // Handle user update from LawyerRow component
  const handleUserUpdate = (updatedUser) => {
    console.log("updated user:", updatedUser);
    setUsers((prevUsers) =>
      prevUsers.map((user) => (user.id === updatedUser.id ? updatedUser : user))
    );
  };

  // Handle showing add user form
  const handleShowAddUserForm = () => {
    setShowAddUserForm(true);
    setNewUserData({
      email: "",
      password: "",
      role: 1,
      roleName: "Lawyer",
    });
  };

  // Handle canceling add user form
  const handleCancelAddUser = () => {
    setShowAddUserForm(false);
    setNewUserData({
      email: "",
      password: "",
      role: 1,
      roleName: "Lawyer",
    });
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUserData((prev) => ({
      ...prev,
      [name]: value,
      // Update role name when role changes
      roleName: name === "role" ? getRoleName(parseInt(value)) : prev.roleName,
    }));
  };

  // Get role name from role number
  const getRoleName = (roleNumber) => {
    const roleMap = {
      1: "Lawyer",
      2: "Admin",
      3: "Super Admin",
    };
    return roleMap[roleNumber] || "Lawyer";
  };

  // Handle creating new user
  const handleCreateUser = async (e) => {
    e.preventDefault();

    if (!newUserData.email || !newUserData.password) {
      toast.error("Please fill in all required fields", {
        position: "top-right",
        autoClose: 3000,
      });
      return;
    }

    setCreatingUser(true);
    try {
      const response = await api.post(API_ENDPOINTS.AUTH.REGISTER, {
        email: newUserData.email,
        password: newUserData.password,
        role: parseInt(newUserData.role),
      });

      if (response.data.success) {
        // Transform the new user data to match our component structure
        const newUser = {
          id: response.data.lawyer._id,
          name: response.data.lawyer.email.split("@")[0],
          email: response.data.lawyer.email,
          role: response.data.lawyer.role,
          roleLabel: response.data.lawyer.role_name,
          lastActive: "N/A",
          assignedClients: 0,
          assignedClientsList: [],
          createdAt: response.data.lawyer.created_at || "N/A",
          rawData: response.data.lawyer,
        };

        // Add new user to the list
        setUsers((prevUsers) => [...prevUsers, newUser]);

        toast.success(`User "${newUserData.email}" created successfully`, {
          position: "top-right",
          autoClose: 3000,
        });

        // Reset form and hide it
        setShowAddUserForm(false);
        setNewUserData({
          email: "",
          password: "",
          role: 1,
          roleName: "Lawyer",
        });
      }
    } catch (err) {
      console.error("Failed to create user:", err);
      toast.error(
        err.response?.data?.message ||
          "Failed to create user. Please try again.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    } finally {
      setCreatingUser(false);
    }
  };

  // Handle opening delete confirmation
  const handleDeleteLawyer = (lawyerId, lawyerEmail) => {
    setConfirmingDelete(lawyerId);
  };

  // Handle closing delete confirmation
  const handleCancelDelete = () => {
    setConfirmingDelete(null);
  };

  // Handle confirming deletion
  const handleConfirmDelete = async (lawyerId, lawyerEmail) => {
    setDeletingUser(lawyerId);
    try {
      const response = await api.post(
        `${API_ENDPOINTS.LAWYERS.DELETE}/${lawyerId}`
      );

      if (response.data.success) {
        // Remove user from the list
        setUsers((prevUsers) =>
          prevUsers.filter((user) => user.id !== lawyerId)
        );

        toast.success(`Lawyer "${lawyerEmail}" deleted successfully`, {
          position: "top-right",
          autoClose: 3000,
        });

        // Close the confirmation
        setConfirmingDelete(null);
      }
    } catch (err) {
      console.error("Failed to delete lawyer:", err);
      toast.error(
        err.response?.data?.message ||
          "Failed to delete lawyer. Please try again.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    } finally {
      setDeletingUser(null);
    }
  };

  // Handle opening role edit
  const handleEditRole = (userId, currentRole) => {
    setEditingRole(userId);
    setNewRoleData({
      role: currentRole,
      roleName: getRoleName(currentRole),
    });
  };

  // Handle closing role edit
  const handleCancelRoleEdit = () => {
    setEditingRole(null);
    setNewRoleData({ role: 1, roleName: "Lawyer" });
  };

  // Handle role change
  const handleRoleChange = (e) => {
    const newRole = parseInt(e.target.value);
    setNewRoleData({
      role: newRole,
      roleName: getRoleName(newRole),
    });
  };

  // Handle updating user role
  const handleUpdateRole = async (userId, userEmail) => {
    if (updatingRole === userId) return;

    const { role } = newRoleData;
    const user = users.find((u) => u.id === userId);

    if (user.role === role) {
      // No change needed
      handleCancelRoleEdit();
      return;
    }

    setUpdatingRole(userId);
    try {
      const response = await api.put(
        `${API_ENDPOINTS.LAWYERS.UPDATE_ROLE}/${userId}`,
        {
          role: role,
        }
      );

      if (response.data.success) {
        // Update user in the list
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === userId
              ? {
                  ...user,
                  role: role,
                  roleLabel: getRoleName(role),
                }
              : user
          )
        );

        toast.success(`Role updated successfully for "${userEmail}"`, {
          position: "top-right",
          autoClose: 3000,
        });

        // Close the edit mode
        handleCancelRoleEdit();
      }
    } catch (err) {
      console.error("Failed to update role:", err);
      toast.error(
        err.response?.data?.message ||
          "Failed to update role. Please try again.",
        {
          position: "top-right",
          autoClose: 5000,
        }
      );
    } finally {
      setUpdatingRole(null);
    }
  };

  if (loading) {
    return (
      <div className="user-management">
        <div className="section-header">
          <h3>User Management</h3>
        </div>
        <div className="loading-container">
          <p>Loading users...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="user-management">
        <div className="section-header">
          <h3>User Management</h3>
        </div>
        <div className="error-container">
          <p className="error-message">{error}</p>
          <button
            className="btn-primary"
            onClick={fetchUserData}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="user-management">
      <div className="section-header">
        <h3>User Management</h3>
        <button
          className="btn-primary"
          onClick={handleShowAddUserForm}
          disabled={showAddUserForm}>
          {showAddUserForm ? "Adding User..." : "Add New User"}
        </button>
      </div>

      {showAddUserForm && (
        <div className="add-user-form-container">
          <div className="add-user-form">
            <div className="form-header">
              <h4>Add New User</h4>
              <button
                className="close-form-btn"
                onClick={handleCancelAddUser}
                disabled={creatingUser}>
                ×
              </button>
            </div>
            <form onSubmit={handleCreateUser}>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={newUserData.email}
                    onChange={handleInputChange}
                    placeholder="Enter email address"
                    required
                    disabled={creatingUser}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <input
                    type="text"
                    id="password"
                    name="password"
                    value={newUserData.password}
                    onChange={handleInputChange}
                    placeholder="Enter password "
                    required
                    minLength="6"
                    disabled={creatingUser}
                  />
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="role">Role</label>
                  <select
                    id="role"
                    name="role"
                    value={newUserData.role}
                    onChange={handleInputChange}
                    disabled={creatingUser}>
                    <option value={1}>Lawyer</option>
                    <option value={2}>Admin</option>
                    <option value={3}>Super Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label>Role Name</label>
                  <input
                    type="text"
                    value={newUserData.roleName}
                    disabled
                    className="role-name-display"
                  />
                </div>
              </div>
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={
                    creatingUser || !newUserData.email || !newUserData.password
                  }>
                  {creatingUser ? "Creating User..." : "Create User"}
                </button>
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={handleCancelAddUser}
                  disabled={creatingUser}>
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="user-stats">
        <div className="stat-card">
          <h4>Total Users</h4>
          <p className="stat-number">{users.length}</p>
        </div>
        <div className="stat-card">
          <h4>Admins</h4>
          <p className="stat-number">
            {users.filter((user) => user.role >= 2).length}
          </p>
        </div>
      </div>

      <div className="user-table-container">
        <table className="user-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Assigned Clients</th>
              <th>Actions</th>
              {currentUser?.role === 3 && <th>Edit Role</th>}
              {currentUser?.role === 3 && <th>Delete</th>}
            </tr>
          </thead>
          <tbody>
            {users.length === 0 ? (
              <tr>
                <td
                  colSpan={currentUser?.role === 3 ? "7" : "5"}
                  style={{
                    textAlign: "center",
                    padding: "20px",
                    color: "var(--text-secondary)",
                  }}>
                  No lawyers found
                </td>
              </tr>
            ) : (
              users.map((user) => (
                <LawyerRow
                  key={user.id}
                  user={user}
                  clients={clients}
                  currentUser={currentUser}
                  isExpanded={isRowExpanded(user.id)}
                  onToggleExpansion={toggleRowExpansion}
                  onUserUpdate={handleUserUpdate}
                  onDeleteLawyer={handleDeleteLawyer}
                  onConfirmDelete={handleConfirmDelete}
                  onCancelDelete={handleCancelDelete}
                  isDeletingUser={deletingUser === user.id}
                  isConfirmingDelete={confirmingDelete === user.id}
                  onEditRole={handleEditRole}
                  onUpdateRole={handleUpdateRole}
                  onCancelRoleEdit={handleCancelRoleEdit}
                  onRoleChange={handleRoleChange}
                  isEditingRole={editingRole === user.id}
                  isUpdatingRole={updatingRole === user.id}
                  newRoleData={newRoleData}
                />
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default UserManagement;
